// client/src/components/admin/AdminAnalytics.js
import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import PageLayout from '../common/layout/PageLayout'
import PrimaryMetricsCards from './PrimaryMetricsCards'
import EventDetailsModal from './analytics/EventDetailsModal'
import HealthMonitoringPanel from './analytics/HealthMonitoringPanel'
import EventsTableSection from './analytics/EventsTableSection'
import MetricsDashboardSection from './analytics/MetricsDashboardSection'
import toastr from 'toastr'
import './AdminAnalytics.css'
import { copyToClipboard } from '../../utils/analyticsHelpers'
import useAnalyticsData from '../../hooks/useAnalyticsData'
import useAnalyticsFilters from '../../hooks/useAnalyticsFilters'
import useEventsPagination from '../../hooks/useEventsPagination'
import { useDebounce } from '../../hooks/useDebounce'

function AdminAnalytics() {
  // NOTE: This component must be rendered within a <Router> context.
  const navigate = useNavigate()

  // Custom hooks for state management
  const data = useAnalyticsData()
  const filters = useAnalyticsFilters()
  const pagination = useEventsPagination()

  // Local UI state (not managed by hooks)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [showEventModal, setShowEventModal] = useState(false)
  const [flowSearchTerm, setFlowSearchTerm] = useState('')
  const debouncedFlowSearch = useDebounce(flowSearchTerm, 1000)

  // Initialize data on component mount
  // Sequence critical loads to reduce database pool pressure
  useEffect(() => {
    const loadInitialData = async () => {
      // Load lightweight data first
      await data.loadPlayers()
      await data.loadHealthData()

      // Then load metrics (which does 3 parallel queries internally)
      const days = parseInt(filters.dateFilter)
      await data.loadMetrics(days)

      // Load remaining data in parallel but after critical metrics
      Promise.all([
        data.loadDailyActiveUsersDetailed(days),
        data.loadEventsByUser(days),
        data.loadFlowData(days, { search: debouncedFlowSearch.trim() }),
      ])
    }

    loadInitialData()

    // Auto-refresh health data every 2 minutes
    const healthInterval = setInterval(data.loadHealthData, 2 * 60 * 1000)

    return () => clearInterval(healthInterval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Refresh metrics when date filter changes
  useEffect(() => {
    const days = parseInt(filters.dateFilter)

    // Sequence loads to avoid pool pressure
    data.loadMetrics(days)
    data.loadDailyActiveUsersDetailed(days)
    data.loadEventsByUser(days)
    data.loadFlowData(days, { search: debouncedFlowSearch.trim() })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.dateFilter, debouncedFlowSearch])

  const flowFilterHint = useMemo(
    () =>
      'Filters User Journey Flow only (Landing flow hidden when filtered). Search by league, division, player, email, or user ID.',
    [],
  )

  // Refresh events when filters change
  useEffect(() => {
    pagination.reset()
    data.loadEvents(filters.selectedPlayerId, filters.getEventFilters(), 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.selectedPlayerId,
    filters.hideApiRequests,
    filters.excludeAdminUsers,
    filters.getEventFilters,
    data.loadEvents,
    pagination.reset,
  ])

  const handleDateFilterChange = (e) => {
    filters.setDateFilter(e.target.value)
  }

  // Player filter handlers (delegated to filters hook)
  const handlePlayerSelect = (playerId) => {
    filters.setSelectedPlayerId(playerId)
  }

  const handleClearPlayerFilter = () => {
    filters.clearPlayerFilter()
  }

  // Get filtered players based on search term (delegated to filters hook)
  const getFilteredPlayers = () => {
    return filters.getFilteredPlayers(data.players)
  }

  // Get selected player name (delegated to filters hook)
  const getSelectedPlayerName = () => {
    return filters.getSelectedPlayerName(data.players)
  }

  // Pagination handler for loading more events
  const loadMoreEvents = () => {
    pagination.loadMore(async (newOffset) => {
      return await data.loadMoreEvents(
        filters.selectedPlayerId,
        filters.getEventFilters(),
        newOffset,
      )
    })
  }

  // Modal functions for event details
  const openEventModal = (event) => {
    setSelectedEvent(event)
    setShowEventModal(true)
  }

  const closeEventModal = () => {
    setShowEventModal(false)
    setSelectedEvent(null)
  }

  // Event action handlers for mobile dropdown
  const handleCopyId = (eventId) => {
    if (eventId) {
      copyToClipboard(String(eventId))
    } else {
      toastr.error('Event ID not available')
    }
  }

  const handleCopyPayload = (event) => {
    copyToClipboard(JSON.stringify(event, null, 2))
  }

  const handleJumpToUser = useCallback(
    (userId) => {
      if (userId) {
        navigate(`/admin/users?userId=${userId}`)
      }
    },
    [navigate],
  )

  // Handle ESC key and click outside modal
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && showEventModal) {
        closeEventModal()
      }
    }

    if (showEventModal) {
      document.addEventListener('keydown', handleEscKey)
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey)
      document.body.style.overflow = 'unset'
    }
  }, [showEventModal])

  if (data.metricsLoading) {
    return (
      <PageLayout title="Analytics Dashboard" noCard={true}>
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading analytics data...</p>
        </div>
      </PageLayout>
    )
  }

  if (data.error) {
    return (
      <PageLayout title="Analytics Dashboard" noCard={true}>
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error Loading Analytics</h4>
          <p>{data.error}</p>
          <button
            className="btn btn-outline-danger"
            onClick={() => {
              const days = parseInt(filters.dateFilter)
              data.loadMetrics(days)
              data.loadEvents(
                filters.selectedPlayerId,
                filters.getEventFilters(),
                0,
              )
            }}
          >
            Try Again
          </button>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout title="Analytics Dashboard" noCard={true}>
      {/* Primary Health Metrics Cards */}
      <PrimaryMetricsCards />

      {/* Metrics Dashboard */}
      <MetricsDashboardSection
        metrics={data.metrics}
        dailyActiveUsersDetailed={data.dailyActiveUsersDetailed}
        dailyActiveUsersLoading={data.dailyActiveUsersLoading}
        players={data.players}
        eventsByUser={data.eventsByUser}
        eventsByUserLoading={data.eventsByUserLoading}
        flowData={data.flowData}
        flowLoading={data.flowLoading}
        flowError={data.flowError}
        dateFilter={filters.dateFilter}
        onDateFilterChange={handleDateFilterChange}
        onJumpToUser={handleJumpToUser}
        flowSearchTerm={flowSearchTerm}
        onFlowSearchChange={(value) => setFlowSearchTerm(value)}
        flowSearchHint={flowFilterHint}
        onRefreshFlow={() =>
          data.loadFlowData(parseInt(filters.dateFilter), {
            search: debouncedFlowSearch.trim(),
          })
        }
      />

      {/* Recent Events Table */}
      <EventsTableSection
        events={data.events}
        isLoading={data.eventsLoading}
        isLoadingMore={pagination.loadingMore}
        hasMore={pagination.hasMore}
        onLoadMore={loadMoreEvents}
        onEventClick={openEventModal}
        onCopyId={handleCopyId}
        onCopyPayload={handleCopyPayload}
        onJumpToUser={handleJumpToUser}
        filterControls={{
          subtitle: filters.selectedPlayerId
            ? `Events for ${getSelectedPlayerName()}`
            : 'Last 100 system events',
          content: (
            <>
              <div className="position-relative player-filter-wrapper">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search player by name or user ID..."
                  value={filters.playerSearchTerm}
                  onChange={(e) => filters.setPlayerSearchTerm(e.target.value)}
                  disabled={data.playersLoading}
                />

                {/* Player Dropdown: only show when searching, not on initial load */}
                {filters.playerSearchTerm.trim().length > 0 && (
                  <div
                    className="position-absolute w-100"
                    style={{ top: '100%', zIndex: 1050 }}
                  >
                    <div className="bg-white border rounded shadow-sm mt-1 max-height-200 overflow-auto">
                      {data.playersLoading ? (
                        <div className="p-2 text-center text-muted">
                          <small>Loading players...</small>
                        </div>
                      ) : getFilteredPlayers().length > 0 ? (
                        getFilteredPlayers().map((player) => (
                          <button
                            key={player.id}
                            className="btn btn-link btn-sm w-100 text-start border-0 rounded-0"
                            onClick={() =>
                              handlePlayerSelect(player.user_id?.toString())
                            }
                            style={{ textDecoration: 'none' }}
                          >
                            <small>
                              <strong>
                                {`${player.first_name || ''} ${
                                  player.last_name || ''
                                }`.trim() || 'Unnamed'}
                              </strong>
                              <span className="text-muted ms-1">
                                (User {player.user_id})
                              </span>
                            </small>
                          </button>
                        ))
                      ) : filters.playerSearchTerm ? (
                        <div className="p-2 text-center text-muted">
                          <small>No players found</small>
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>

              {filters.selectedPlayerId && (
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleClearPlayerFilter}
                  title="Clear player filter"
                >
                  ✕ Clear
                </button>
              )}

              {/* Toggle to hide/show api_request events */}
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="hideApiRequestsToggle"
                  checked={filters.hideApiRequests}
                  onChange={(e) => filters.setHideApiRequests(e.target.checked)}
                />
                <label
                  className="form-check-label"
                  htmlFor="hideApiRequestsToggle"
                  style={{ fontSize: '0.875rem' }}
                >
                  Hide API Requests
                </label>
              </div>

              {/* Toggle to exclude admin/owner users */}
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="excludeAdminUsersToggle"
                  checked={filters.excludeAdminUsers}
                  onChange={(e) =>
                    filters.setExcludeAdminUsers(e.target.checked)
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="excludeAdminUsersToggle"
                  style={{ fontSize: '0.875rem' }}
                  title="Hide events from admin and owner users to focus on player behavior. Events with no user_id (anonymous) are still included."
                >
                  Exclude Admin Users
                </label>
              </div>
            </>
          ),
        }}
      />

      {/* System Health Status */}
      <HealthMonitoringPanel
        healthData={data.healthData}
        performanceData={data.performanceData}
        isLoading={data.healthLoading}
        onRefresh={data.loadHealthData}
      />

      {/* Event Details Modal */}
      <EventDetailsModal
        isOpen={showEventModal}
        onClose={closeEventModal}
        event={selectedEvent}
        onJumpToUser={handleJumpToUser}
      />
    </PageLayout>
  )
}

export default AdminAnalytics
